import {
  Typography,
  Box,
  Chip,
  Avatar,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Tooltip,
} from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { SessionSummary } from "src/store/api/generatedApi";
import {
  hoverEmail,
  userDisplayName,
  userInitials,
} from "src/utils/userDisplay";
import { useNavigateToSearch } from "hooks/navigation";
import { SearchStatusChip } from "../search/common/SearchStatus";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import AccessTime from "@mui/icons-material/AccessTime";

dayjs.extend(relativeTime);

const formatTimeAgo = (dateString: string) => {
  return dayjs(dateString).fromNow();
};

const getStatusChips = (search: SessionSummary) => {
  const { searches_failed, searches_succeeded, searches_running } = search;

  return (
    <Stack direction="row" spacing={1}>
      {searches_running > 0 && (
        <SearchStatusChip searchStatus="running" count={searches_running} />
      )}
      {searches_failed > 0 && (
        <SearchStatusChip searchStatus="failure" count={searches_failed} />
      )}
      {searches_succeeded > 0 && (
        <SearchStatusChip searchStatus="success" count={searches_succeeded} />
      )}
    </Stack>
  );
};

export const RecentSearchesRow = ({ search }: { search: SessionSummary }) => {
  const { t } = useTenantTranslation();
  const navigate = useNavigateToSearch();
  return (
    <Box>
      <ListItem
        sx={{
          py: 1,
          cursor: "pointer",
          "&:hover": {
            bgcolor: "action.hover",
          },
          transition: "background-color 0.2s ease-in-out",
        }}
        onClick={() => {
          navigate(search.last_search_id);
        }}
      >
        <ListItemAvatar>
          <Tooltip title={hoverEmail(search.user)}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                fontSize: "0.75rem",
                fontWeight: "fontWeightSemiBold",
              }}
            >
              {userInitials(search.user)}
            </Avatar>
          </Tooltip>
        </ListItemAvatar>
        <ListItemText
          primary={
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: 0.5,
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: "fontWeightMedium",
                  color: "text.primary",
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {search.name}
              </Typography>
              {getStatusChips(search)}
            </Box>
          }
          secondary={
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
              component="span"
            >
              <span>
                {userDisplayName(search.user)} • {search.search_count}{" "}
                {t("searches")}
              </span>
              {search.deployment_history.length > 0 && (
                <Chip
                  label={t("deployed")}
                  size="small"
                  variant="outlined"
                  sx={{
                    ml: 1,
                    height: 16,
                    fontSize: "0.6rem",
                  }}
                  component="span"
                />
              )}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
                component="span"
              >
                <AccessTime
                  sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                />
                <span>{formatTimeAgo(search.created_at)}</span>
              </Box>
            </Box>
          }
        />
      </ListItem>
    </Box>
  );
};
